import { useCallback, useState } from 'react';
import type React from 'react';

type ModifierEvent = React.KeyboardEvent | React.MouseEvent;

export interface CapsLockFieldProps {
  onKeyDown: (event: React.KeyboardEvent) => void;
  onKeyUp: (event: React.KeyboardEvent) => void;
  onClick: (event: React.MouseEvent) => void;
  onBlur: () => void;
}

/**
 * Tracks Caps Lock per password field so only the focused one shows the warning.
 * Browsers expose the state only on key/mouse events, so it appears after the first
 * keystroke or click in the field (not on page load).
 */
export const useCapsLockWarning = () => {
  const [activeField, setActiveField] = useState<string | null>(null);

  const capsLockProps = useCallback((field: string): CapsLockFieldProps => {
    const update = (event: ModifierEvent) => {
      const isOn = event.getModifierState('CapsLock');
      setActiveField((prev) => (isOn ? field : prev === field ? null : prev));
    };
    return {
      onKeyDown: update,
      onKeyUp: update,
      onClick: update,
      onBlur: () => setActiveField((prev) => (prev === field ? null : prev)),
    };
  }, []);

  const isCapsLockOn = useCallback((field: string) => activeField === field, [activeField]);

  return { capsLockProps, isCapsLockOn };
};
